import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';

import { combineLatest, Observable, Subscription } from 'rxjs';
import { Store } from '@ngrx/store';
import { take } from 'rxjs/operators';

import { FinnairBoundItem, FinnairOrder, FinnairTravelEndpoint } from '@fcom/dapi/api/models';
import { ButtonLayout, ButtonPosition } from '@fcom/ui-components';
import { finShare } from '@fcom/rx';
import { GtmService } from '@fcom/common/gtm';
import { ElementActions, ElementTypes, GaContext } from '@fcom/common';
import { getTails } from '@fcom/common-booking/utils/common-booking.utils';
import { VoluntaryChangeService } from '@fcom/voluntary-change/services/voluntary-change.service';
import { VOLUNTARY_CHANGE_STEPS } from '@fcom/voluntary-change/voluntary-change-config';
import { NotificationsService } from '@fcom/manage-booking/services';
import { AppState } from '@fcom/core/interfaces/app-state.interface';
import { orderData, getChangeableBoundIds } from '@fcom/common-booking';
import { UtilsService } from '@fcom/common-booking/services/utils.service';
import { Airport } from '@fcom/common/interfaces/booking';

import { ChangeActions } from '../../../../store/actions/change.actions';
import { getChangeBoundIds } from '../../../../store/selectors/change.selector';
import { getTrackingDescription } from '../../../../utils';

@Component({
  selector: 'fin-mmb-select-bound',
  templateUrl: './select-bound.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ManageBookingSelectBoundComponent implements OnInit, OnDestroy {
  readonly ButtonPosition = ButtonPosition;
  readonly ButtonLayout = ButtonLayout;
  readonly getTails = getTails;

  changeableBoundIds$: Observable<string[]>;
  boundNotifications$: Observable<{ [flightId: string]: string }>;

  selectedBoundsForChange$: Observable<string[]>;
  booking$: Observable<FinnairOrder>;
  subscriptions = new Subscription();

  constructor(
    private store$: Store<AppState>,
    private utilsService: UtilsService,
    private gtmService: GtmService,
    private voluntaryChangeService: VoluntaryChangeService,
    private notificationsService: NotificationsService
  ) {}

  ngOnInit(): void {
    this.booking$ = this.store$.pipe(orderData(), finShare());
    this.changeableBoundIds$ = this.store$.pipe(getChangeableBoundIds());
    this.boundNotifications$ = this.notificationsService.getChangeNotAllowedReasonsByBound();
    this.selectedBoundsForChange$ = this.store$.pipe(getChangeBoundIds());
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  getLocation(leg: FinnairTravelEndpoint): Observable<Airport> {
    return this.utilsService.getLocation(leg.locationCode, leg.dateTime);
  }

  toggleOutbound(bound: FinnairBoundItem): void {
    this.store$.dispatch(ChangeActions.toggleOutbound({ bound }));
  }

  toggleInbound(bound: FinnairBoundItem): void {
    this.store$.dispatch(ChangeActions.toggleInbound({ bound }));
  }

  continue(): void {
    this.subscriptions.add(
      combineLatest([this.booking$, this.changeableBoundIds$, this.selectedBoundsForChange$])
        .pipe(take(1))
        .subscribe(([booking, changeableBoundIds, selectedBoundsForChange]) => {
          this.gtmService.trackElement(
            `view-pnr_change_select-${getTrackingDescription(changeableBoundIds, selectedBoundsForChange)}`,
            GaContext.MANAGE_BOOKING,
            ElementTypes.BUTTON,
            undefined,
            ElementActions.CLICK,
            `recLoc-${booking?.otherInformation.analyticsToken}`
          );
          this.voluntaryChangeService.setStep({ step: VOLUNTARY_CHANGE_STEPS.SELECT_DATES });
        })
    );
  }
}
